<script>
  import { onMount } from 'svelte';
  import { getApiUrl } from '../api';
  import { authFetch } from '../authService';
  import { t } from '../i18nStore';
  import { getCurrencySymbol, formatDate } from '../utils';
  import { selectedCountry } from '../countryStore';

  let receipts = [];
  let isLoading = true;
  let page = 1;
  const pageSize = 12;
  let totalPages = 1;
  
  $: currency = getCurrencySymbol($selectedCountry);

  onMount(() => fetchReceipts());

  async function fetchReceipts() {
      isLoading = true;
      try {
          const res = await authFetch(getApiUrl(`/admin/transactions/receipts?page=${page}&pageSize=${pageSize}`));
          const result = await res.json();
          if (result && result.data) {
              receipts = result.data;
              totalPages = result.totalPages || 1;
          } else {
              receipts = [];
          }
      } catch(e) {
          console.error(e);
      } finally {
          isLoading = false;
      }
  }

  async function updateStatus(transactionId, status) {
      try {
          const res = await authFetch(getApiUrl('/admin/transactions/verify'), {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ transactionId, status })
          });
          if (res.ok) {
              receipts = receipts.map(r => r._id === transactionId ? { ...r, verificationStatus: status } : r);
          } else {
              alert('Failed to update status');
          }
      } catch (e) {
          console.error(e);
      }
  }

  function nextPage() { if (page < totalPages) { page++; fetchReceipts(); } }
  function prevPage() { if (page > 1) { page--; fetchReceipts(); } }
</script>

<div class="admin-container">
  <h2>{$t('admin.receipts.title')}</h2>
  
  {#if isLoading}
      <p class="loading">{$t('common.loading')}</p>
  {:else if receipts.length === 0}
      <p class="no-data">{$t('admin.receipts.no_data')}</p>
  {:else}
      <div class="receipt-grid">
          {#each receipts as tx}
              <div class="receipt-card">
                  <!-- Thumbnail linking to full image -->
                  <a href={tx.receiptImageUrl} target="_blank" rel="noopener noreferrer" class="img-wrapper">
                      <img src={tx.receiptImageUrl} alt="Receipt" loading="lazy" />
                      <div class="overlay-hint">🔍 View Full Size</div>
                  </a>

                  <div class="details">
                      <p><strong>{$t('admin.receipts.user')}:</strong> {tx.userName || 'Unknown'}</p>
                      <p><strong>{$t('admin.receipts.promotion')}:</strong> {tx.promotionName || 'Unknown'}</p>
                      <p><strong>{$t('admin.receipts.date')}:</strong> {formatDate(tx.transactionDateTime)}</p>
                      <p><strong>{$t('admin.receipts.amount')}:</strong> {currency}{(tx.amount || 0).toFixed(2)}</p>
                      <p class="status-row">
                          <strong>Status:</strong> 
                          <span class="badge {tx.verificationStatus ? tx.verificationStatus.toLowerCase() : 'pending'}">
                              {tx.verificationStatus ? $t(`admin.receipts.status_${tx.verificationStatus.toLowerCase().replace('/', '')}`) : $t('admin.receipts.status_pending')}
                          </span>
                      </p>
                  </div>

                  <div class="actions">
                      <button class="btn btn-verify" on:click={() => updateStatus(tx._id, 'Verified')}>{$t('admin.receipts.btn_verify')}</button>
                      <button class="btn btn-reject" on:click={() => updateStatus(tx._id, 'Rejected')}>{$t('admin.receipts.btn_reject')}</button>
                      <button class="btn btn-na" on:click={() => updateStatus(tx._id, 'N/A')}>{$t('admin.receipts.btn_na')}</button>
                  </div>
              </div>
          {/each}
      </div>

      {#if totalPages > 1}
          <div class="pagination">
              <button disabled={page <= 1} on:click={prevPage}>{$t('common.prev')}</button>
              <span>{$t('common.page')} {page} {$t('common.of')} {totalPages}</span>
              <button disabled={page >= totalPages} on:click={nextPage}>{$t('common.next')}</button>
          </div>
      {/if}
  {/if}
</div>

<style>
  .admin-container { max-width: 1200px; margin: 2rem auto; padding: 0 1rem; }
  h2 { color: #333; margin-bottom: 1.5rem; }
  
  .receipt-grid {
      display: grid;
      grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
      gap: 1.5rem;
  }
  
  .receipt-card {
      background: white; border-radius: 8px; box-shadow: 0 2px 8px rgba(0,0,0,0.1); 
      overflow: hidden; display: flex; flex-direction: column;
  }
  
  .img-wrapper {
      position: relative; width: 100%; height: 200px; background: #e9ecef; 
      display: block; text-decoration: none; overflow: hidden;
  }
  .img-wrapper img { width: 100%; height: 100%; object-fit: cover; transition: transform 0.3s; }
  .img-wrapper:hover img { transform: scale(1.05); }
  
  .overlay-hint {
      position: absolute; bottom: 0; left: 0; width: 100%; padding: 8px;
      background: rgba(0,0,0,0.6); color: white; text-align: center; 
      font-size: 0.85rem; font-weight: bold; opacity: 0; transition: opacity 0.2s;
  }
  .img-wrapper:hover .overlay-hint { opacity: 1; }

  .details { padding: 1rem; flex-grow: 1; font-size: 0.9rem; color: #444; }
  .details p { margin: 0.4rem 0; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
  
  .status-row { display: flex; align-items: center; gap: 0.5rem; margin-top: 0.8rem !important; }
  .badge { padding: 3px 8px; border-radius: 12px; font-size: 0.75rem; font-weight: bold; text-transform: uppercase; color: white;}
  .badge.verified { background: #28a745; }
  .badge.rejected { background: #dc3545; }
  .badge.na { background: #6c757d; }
  .badge.pending { background: #ffc107; color: #333; }

  .actions { display: flex; gap: 0.5rem; padding: 1rem; border-top: 1px solid #eee; background: #fafafa;}
  .btn { padding: 8px; border-radius: 4px; border: none; cursor: pointer; color: white; font-weight: bold; flex: 1; font-size: 0.85rem; transition: opacity 0.2s; }
  .btn:hover { opacity: 0.85; }
  .btn-verify { background: #28a745; }
  .btn-reject { background: #dc3545; }
  .btn-na { background: #6c757d; }

  .loading, .no-data { text-align: center; color: #666; font-style: italic; padding: 3rem 0; font-size: 1.1rem;}

  .pagination { display: flex; justify-content: center; align-items: center; gap: 1rem; margin-top: 2rem; }
  .pagination button { background: #e9ecef; border: none; padding: 0.5rem 1.2rem; border-radius: 4px; cursor: pointer; color: #333; font-weight: 500;}
  .pagination button:hover:not(:disabled) { background: #dee2e6; }
  .pagination button:disabled { opacity: 0.5; cursor: not-allowed; }
</style>